/**
 * Installs Live — read-only selector.
 * ────────────────────────────────────
 * Joins canonical Job records with their JobSpecExecution to surface every
 * install currently in flight today. Pure: no writes, no side effects, no
 * computation outside what canonical resolvers already expose.
 *
 * Hard rules (per Execution Domain lock + scope):
 *  1. NEVER reference contractValue or payoutAmount in any field returned.
 *  2. NEVER fabricate phase progress when JobSpecExecution data is missing
 *     or partial — returned row carries `executionAvailable: false` so the
 *     UI can render a fallback row instead of fake progress numbers.
 *  3. No mutations. Selector is a pure function over inputs.
 */

import type { Job } from "../types";
import type { BoundJobSpec, JobSpecPhaseName } from "../jobSpec";
import { resolveBoundSpec } from "../jobSpec";
import type { JobSpecExecution, PhaseExecution, PhaseStatus } from "../jobSpecExecution";
import { phaseProgressPct, getActivePhase } from "../jobSpecExecution";

export interface InstallsLivePhaseSummary {
  phaseName: JobSpecPhaseName;
  status: PhaseStatus;
  photosUploaded: number;
  photosRequired: number;
  checklistChecked: number;
  checklistTotal: number;
  progressPct: number;
  lastActivityAt?: string;
  reconciliationPending: boolean;
}

export interface InstallsLiveRow {
  jobId: string;
  jobLabel: string;
  customerName: string;
  city: string;
  installerName: string;
  scheduledInstallDate: string;
  /** True when the row has resolvable spec + execution data. */
  executionAvailable: boolean;
  /** Reason copy for UI when executionAvailable=false. */
  unavailableReason?: string;
  activePhaseName?: JobSpecPhaseName;
  activePhaseStatus?: PhaseStatus;
  /** All phases in spec order (only present when executionAvailable=true). */
  phases: InstallsLivePhaseSummary[];
  /** Aggregated counts (only meaningful when executionAvailable=true). */
  totalPhotosUploaded: number;
  totalPhotosRequired: number;
  totalChecklistChecked: number;
  totalChecklistTotal: number;
  /** Most-recent activity timestamp across all phases. */
  lastActivityAt?: string;
  /** True when at least one phase is awaiting QA review. */
  awaitingQa: boolean;
  /** True when at least one phase has a reconciliation gate pending. */
  reconciliationPending: boolean;
  /** Stale = no activity beyond the configured threshold while a phase is in_progress/submitted. */
  isStale: boolean;
  /** Total qa_failed phases — surfaces "phase artifact" count for QA failures. */
  qaFailuresTotal: number;
  /** Milliseconds since lastActivityAt (undefined when no activity). UI sort key. */
  staleAgeMs?: number;
  /** Bound spec for downstream QA panel (read-only pass-through). */
  bound?: BoundJobSpec;
}

export interface InstallsLiveSelectorInput {
  jobs: Job[];
  specExecutions: JobSpecExecution[];
  now?: Date;
  /**
   * Configurable stale threshold (ms). A row is marked stale when there's
   * an in-flight phase and last activity is older than this. Defaults to
   * 4 hours when not provided. Operator-tunable from the page UI.
   */
  staleThresholdMs?: number;
}

const DEFAULT_STALE_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Stages that indicate a job is actively in execution regardless of
 * scheduledInstallDate or phase execution data. Jobs in these stages
 * always appear in Active Installs so operators don't miss jobs that
 * the portal has pushed into execution without a matching CCC spec exec.
 */
const EXECUTION_ACTIVE_STAGES = new Set([
  "Install In Progress",
  "Install Complete",
  "QA Review",
]);

/**
 * Maps CCC JobSpecPhaseName → portal checklist slug.
 * Portal writes keys as "{slug}:{itemIndex}" in jobs.metadata.checklist.
 */
const PHASE_NAME_TO_PORTAL_SLUG: Partial<Record<JobSpecPhaseName, string>> = {
  "Arrival, Prep & Protection": "prep",
  "Demolition & Structural Assessment": "demo",
  "Framing, Plumbing & Rough-In": "rough",
  "Pan, Substrate & Wall Installation": "install",
  "Trim, Fixtures & Waterproof Finishing": "finish",
  "Final QA, Water Testing & Closeout": "qa",
};

function todayISO(now: Date): string {
  // Local-date YYYY-MM-DD (FL region per Core memory; no TZ math needed for
  // a date-string compare against scheduledInstallDate which is stored as a
  // local-date string in the prototype).
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function lastActivityForPhase(phase: PhaseExecution): string | undefined {
  const candidates = [
    phase.qaReviewedAt,
    phase.completedAt,
    ...phase.photos.map((p) => p.uploadedAt),
    ...phase.checklist.map((c) => c.checkedAt).filter((x): x is string => !!x),
  ].filter((x): x is string => !!x);
  if (!candidates.length) return undefined;
  return candidates.sort().slice(-1)[0];
}

function hasInFlightActivity(execution: JobSpecExecution): boolean {
  return execution.phases.some(
    (p) => p.status === "in_progress" || p.status === "submitted" || p.status === "qa_failed",
  );
}

export function buildInstallsLiveRows(input: InstallsLiveSelectorInput): InstallsLiveRow[] {
  const now = input.now ?? new Date();
  const today = todayISO(now);
  const staleThresholdMs = input.staleThresholdMs ?? DEFAULT_STALE_THRESHOLD_MS;

  // Eligible job set: scheduled today OR has live in-flight phase activity.
  const executionsByJob = new Map<string, JobSpecExecution>();
  for (const e of input.specExecutions) executionsByJob.set(e.jobId, e);

  const eligible = input.jobs.filter((job) => {
    // Exclude terminally-closed jobs.
    if (job.closeoutStatus === "Closed") return false;
    const scheduledToday =
      typeof job.scheduledInstallDate === "string" &&
      job.scheduledInstallDate.startsWith(today);
    const exec = executionsByJob.get(job.id);
    const inFlight = exec ? hasInFlightActivity(exec) : false;
    // Stage-based gate: jobs the portal has pushed into execution always
    // appear even when scheduledInstallDate is a past date or exec data
    // hasn't been synthesized yet (e.g., portal-only jobs mid-install).
    const stageActive = EXECUTION_ACTIVE_STAGES.has(job.currentStage ?? "");
    return scheduledToday || inFlight || stageActive;
  });

  const rows: InstallsLiveRow[] = eligible.map((job) => {
    const exec = executionsByJob.get(job.id);
    const bound = job.boundJobSpec;
    const spec = resolveBoundSpec(bound);

    if (!exec || !bound || !spec) {
      const fallback: InstallsLiveRow = {
        jobId: job.id,
        jobLabel: (job as { jobNumber?: string }).jobNumber ?? job.id,
        customerName: job.customerName,
        city: job.address?.city ?? "",
        installerName: job.assignedSubcontractor || job.assignedCrew || "Unassigned",
        scheduledInstallDate: job.scheduledInstallDate,
        executionAvailable: false,
        unavailableReason: !bound
          ? "No JobSpec bound to this job."
          : !spec
            ? "Bound JobSpec could not be resolved (version missing)."
            : "No installer activity recorded yet.",
        phases: [],
        totalPhotosUploaded: 0,
        totalPhotosRequired: 0,
        totalChecklistChecked: 0,
        totalChecklistTotal: 0,
        awaitingQa: false,
        reconciliationPending: false,
        isStale: false,
        qaFailuresTotal: 0,
        bound,
      };
      return fallback;
    }

    const phases: InstallsLivePhaseSummary[] = spec.phases.map((sp) => {
      const pe = exec.phases.find((p) => p.phaseName === sp.name);
      if (!pe) {
        // Fallback: derive completion from portal's flat checklist (jobs.metadata.checklist).
        // Keys: "{phaseName}:{itemIndex}" → boolean.
        const portalChecklist = job._portalChecklist;
        const portalSlug = PHASE_NAME_TO_PORTAL_SLUG[sp.name];
        if (portalChecklist && portalSlug) {
          const phasePrefix = portalSlug + ":";
          const phaseEntries = Object.entries(portalChecklist).filter(([k]) =>
            k.startsWith(phasePrefix),
          );
          const checklistTotal = phaseEntries.length || sp.checklistItems.length;
          const checklistChecked = phaseEntries.filter(([, v]) => v).length;
          const allDone = checklistTotal > 0 && checklistChecked === checklistTotal;
          const anyDone = checklistChecked > 0;
          const status: PhaseStatus = allDone ? "qa_passed" : anyDone ? "in_progress" : "not_started";
          return {
            phaseName: sp.name,
            status,
            photosUploaded: 0,
            photosRequired: sp.requiredPhotos.length,
            checklistChecked,
            checklistTotal,
            progressPct: checklistTotal > 0 ? Math.round((checklistChecked / checklistTotal) * 100) : 0,
            reconciliationPending: false,
          };
        }
        return {
          phaseName: sp.name,
          status: "not_started" as PhaseStatus,
          photosUploaded: 0,
          photosRequired: sp.requiredPhotos.length,
          checklistChecked: 0,
          checklistTotal: sp.checklistItems.length,
          progressPct: 0,
          reconciliationPending: false,
        };
      }
      const photosRequired = sp.requiredPhotos.length;
      const photosUploaded = sp.requiredPhotos.filter((pt) =>
        pe.photos.some((p) => p.photoType === pt),
      ).length;
      const checklistTotal = sp.checklistItems.length;
      const checklistChecked = pe.checklist.filter((c) => c.checked).length;
      return {
        phaseName: sp.name,
        status: pe.status,
        photosUploaded,
        photosRequired,
        checklistChecked,
        checklistTotal,
        progressPct: phaseProgressPct(pe, sp),
        lastActivityAt: lastActivityForPhase(pe),
        reconciliationPending:
          sp.name === "demo" &&
          pe.status === "qa_passed" &&
          pe.reconciliation?.decision !== "spec_valid",
      };
    });

    const active = getActivePhase(exec, spec);
    const activePhase = active ? phases.find((p) => p.phaseName === active.name) : undefined;

    const totals = phases.reduce(
      (acc, p) => {
        acc.photosUploaded += p.photosUploaded;
        acc.photosRequired += p.photosRequired;
        acc.checklistChecked += p.checklistChecked;
        acc.checklistTotal += p.checklistTotal;
        return acc;
      },
      { photosUploaded: 0, photosRequired: 0, checklistChecked: 0, checklistTotal: 0 },
    );

    const lastActivityCandidates = phases
      .map((p) => p.lastActivityAt)
      .filter((x): x is string => !!x)
      .sort();
    const lastActivityAt = lastActivityCandidates.slice(-1)[0];

    const awaitingQa = phases.some((p) => p.status === "submitted");
    const reconciliationPending = phases.some((p) => p.reconciliationPending);
    const qaFailuresTotal = phases.filter((p) => p.status === "qa_failed").length;

    const staleAgeMs = lastActivityAt
      ? now.getTime() - new Date(lastActivityAt).getTime()
      : undefined;
    const hasActiveWork = phases.some(
      (p) => p.status === "in_progress" || p.status === "submitted",
    );
    const isStale =
      hasActiveWork && staleAgeMs !== undefined && staleAgeMs >= staleThresholdMs;

    return {
      jobId: job.id,
      jobLabel: (job as { jobNumber?: string }).jobNumber ?? job.id,
      customerName: job.customerName,
      city: job.address?.city ?? "",
      installerName: job.assignedSubcontractor || job.assignedCrew || "Unassigned",
      scheduledInstallDate: job.scheduledInstallDate,
      executionAvailable: true,
      activePhaseName: active?.name,
      activePhaseStatus: activePhase?.status,
      phases,
      totalPhotosUploaded: totals.photosUploaded,
      totalPhotosRequired: totals.photosRequired,
      totalChecklistChecked: totals.checklistChecked,
      totalChecklistTotal: totals.checklistTotal,
      lastActivityAt,
      awaitingQa,
      reconciliationPending,
      isStale,
      qaFailuresTotal,
      staleAgeMs,
      bound,
    };
  });

  // Default sort: awaiting QA → stale → reconciliation pending → in flight → other.
  // Page-level sort modes layer on top of this baseline.
  return rows.sort((a, b) => {
    const score = (r: InstallsLiveRow) =>
      (r.awaitingQa ? 1000 : 0) +
      (r.isStale ? 500 : 0) +
      (r.reconciliationPending ? 250 : 0) +
      (r.executionAvailable ? 10 : 0);
    return score(b) - score(a);
  });
}
